// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { I18n } from '@calendly/i18n'
import { Badge } from '@calendly/ui/components/badge'
import { InlineBanner } from '@calendly/ui/components/inline-banner'
import { PageHeader } from '@calendly/ui/components/page-header'
import { useLayoutEffect } from 'react'

import { useGetUserQuery } from 'common'
import { LoadingWrapper } from 'components/core/loading_state'
import { SchedulingAssistantTab } from 'features/account_settings'
import {
  useIsSettingsModalLayout,
  useSettingsModalLayoutOptional,
} from 'features/settings_modal/layout_context'
import {
  useDisableEmailAgentMutation,
  useFetchSchedulingAssistantSettingsQuery,
  usePostSchedulingAssistantSettingMutation,
  SCHEDULING_ASSISTANT_ENABLED_SETTING_NAME,
  SCHEDULING_ASSISTANT_ENABLED_SETTING_TYPE,
  SCHEDULING_ASSISTANT_PRIMARY_EVENT_TYPE_SETTING_NAME,
} from 'hooks/scheduling_assistant/api'
import { getSchedulingAssistantSettingValueByName } from 'hooks/scheduling_assistant/helpers'

export const SchedulingAssistantPage = () => {
  const inSettingsModal = useIsSettingsModalLayout()
  const settingsLayout = useSettingsModalLayoutOptional()
  const { data: userData, isLoading: isUserLoading } = useGetUserQuery()
  const { email, uuid: userUuid } = userData ?? {}

  const {
    data: settings,
    isLoading: areSchedulingAssistantSettingsLoading,
    isFetching: areSchedulingAssistantSettingsFetching,
    isError: hasSchedulingAssistantSettingsError
  } = useFetchSchedulingAssistantSettingsQuery(userUuid, { skip: !userUuid })

  const isSettingsPending = areSchedulingAssistantSettingsLoading
    || (areSchedulingAssistantSettingsFetching && !settings)

  const [postSchedulingAssistantSetting, { isLoading: isDisableSettingLoading }] = usePostSchedulingAssistantSettingMutation({
    fixedCacheKey: 'scheduling-assistant-disable',
  })
  const [disableEmailAgent, { isLoading: isDisableEmailAgentLoading }] = useDisableEmailAgentMutation()

  const isSchedulingAssistantEnabled = getSchedulingAssistantSettingValueByName(
    settings,
    SCHEDULING_ASSISTANT_ENABLED_SETTING_NAME
  ) === 'true'
  const primaryEventTypeUuid = getSchedulingAssistantSettingValueByName(
    settings,
    SCHEDULING_ASSISTANT_PRIMARY_EVENT_TYPE_SETTING_NAME
  )
  const hasEnabledForm = Boolean(
    userUuid
    && email
    && settings
    && isSchedulingAssistantEnabled
    && primaryEventTypeUuid
    && !isUserLoading
    && !isSettingsPending
    && !hasSchedulingAssistantSettingsError
  )

  useLayoutEffect(() => {
    if (!inSettingsModal || settingsLayout?.setFooterSuppressed == null) {
      return undefined
    }

    settingsLayout.setFooterSuppressed(!hasEnabledForm)
    return () => settingsLayout.setFooterSuppressed(false)
  }, [hasEnabledForm, inSettingsModal, settingsLayout])

  const handleDisable = async () => {
    if (!userUuid) return
    try {
      await disableEmailAgent().unwrap()
      // Email agent is disabled. OAuth tokens were revoked
      // and deleted downstream in caf-email. Flip the enabled setting
      // to false now, so the UI reflects the change.
      await postSchedulingAssistantSetting({
        userUuid,
        settingName: SCHEDULING_ASSISTANT_ENABLED_SETTING_NAME,
        settingValue: 'false',
        settingType: SCHEDULING_ASSISTANT_ENABLED_SETTING_TYPE
      }).unwrap()
    } catch (error) {
      // No special error handling needed here
    }
  }

  const headerBadges = isSchedulingAssistantEnabled
    ? (
      <>
        <Badge type="new">Beta</Badge>
        <Badge type="success">{I18n.t('publisher.personal_settings.scheduling_assistant.enabled.badge_label')}</Badge>
      </>
    )
    : (
      <>
        <Badge type="new">Beta</Badge>
        <Badge type="error">{I18n.t('publisher.personal_settings.scheduling_assistant.disabled.badge_label')}</Badge>
      </>
    )

  const enabledSubtitle = isSchedulingAssistantEnabled
    ? I18n.t('publisher.personal_settings.scheduling_assistant.enabled.subtitle', { user_email: email ?? '' })
    : null

  return (
    <LoadingWrapper isLoading={isUserLoading || isSettingsPending}>
      {hasSchedulingAssistantSettingsError ? (
        <InlineBanner
          variant="danger"
          title={I18n.t('publisher.personal_settings.scheduling_assistant.settings_load_error_title')}
          message={I18n.t('publisher.personal_settings.scheduling_assistant.settings_load_error_message')}
          isDismissible={false}
        />
      ) : (
        <>
          {!inSettingsModal && (
            <PageHeader
              eyebrow={I18n.t('publisher.personal_settings.navigation.account_settings')}
              title={I18n.t('publisher.personal_settings.scheduling_assistant.page_header')}
              badge={headerBadges}
              description={enabledSubtitle}
            />
          )}
          <SchedulingAssistantTab
            userUuid={userUuid}
            email={email}
            settings={settings}
            isSchedulingAssistantEnabled={isSchedulingAssistantEnabled}
            primaryEventTypeUuid={primaryEventTypeUuid}
            onDisable={handleDisable}
            isDisableLoading={isDisableEmailAgentLoading || isDisableSettingLoading}
            settingsLayout={settingsLayout}
          />
        </>
      )}
    </LoadingWrapper>
  )
}
